import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "488",
  "slug": "what-is-a-tornado-how-tornadoes-form",
  "title": "What Is a Tornado? How Twisters Form and Why Tornado Alley Exists",
  "excerpt": "Tornadoes are the most violent storms on Earth for their size. Here is how a thunderstorm spins one up, how they are rated, and why the central United States gets so many.",
  "date": "2026-06-19",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1500740516770-92bd004b996e?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx0b3JuYWRvJTIwc3Rvcm0lMjBza3l8ZW58MXwwfHx8MTc4MTg1Njc5MHww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "6 min",
  "author": "EarthGuessr Team",
  "tldr": "A tornado is a violently rotating column of air that connects a thunderstorm to the ground. They form when warm, moist air, cool dry air, and winds that change with height combine inside powerful storms. The central United States, or Tornado Alley, gets so many because its geography brings all those ingredients together.",
  "content": [
    {
      "type": "paragraph",
      "text": "Few things in nature are as concentrated and violent as a tornado. A storm hundreds of kilometres wide can wind itself down into a spinning column just a few hundred metres across, with winds strong enough to flatten houses and toss vehicles. For all their drama, tornadoes follow a clear recipe of ingredients — and the geography of where those ingredients meet explains why some places see them constantly and others almost never."
    },
    {
      "type": "heading",
      "text": "What Is a Tornado?"
    },
    {
      "type": "paragraph",
      "text": "A tornado is a rapidly rotating column of air that is in contact with both the surface of the Earth and the base of a thunderstorm cloud. The visible funnel is made of cloud droplets, dust and debris drawn into the spinning air. If a funnel is rotating but has not reached the ground, it is just a funnel cloud; it only counts as a tornado once that circulation touches down."
    },
    {
      "type": "heading",
      "text": "How Tornadoes Form"
    },
    {
      "type": "paragraph",
      "text": "Most strong tornadoes are born from a special kind of thunderstorm called a supercell, which contains a deep, rotating updraft. Three conditions set the stage. First, warm, moist air near the ground provides the fuel. Second, cooler, drier air above it makes the atmosphere unstable, so warm air rushes upward. Third, and crucially, the wind changes speed and direction with height — a feature called wind shear."
    },
    {
      "type": "paragraph",
      "text": "That shear sets the air rolling horizontally, like an invisible tube turning across the landscape. When a storm's powerful updraft tilts that rolling tube upright and stretches it, the rotation tightens and speeds up, the way a spinning skater pulls in their arms. If that rotation reaches all the way down to the ground, a tornado is born."
    },
    {
      "type": "heading",
      "text": "How Tornadoes Are Measured"
    },
    {
      "type": "paragraph",
      "text": "Because it is impossible to put an instrument inside most tornadoes, their strength is judged after the fact from the damage they cause. The Enhanced Fujita scale runs from EF0 to EF5:"
    },
    {
      "type": "list",
      "items": [
        "EF0 and EF1: weaker tornadoes that break branches, peel roofs and topple weak structures.",
        "EF2 and EF3: strong tornadoes that tear roofs off houses and uproot large trees.",
        "EF4 and EF5: the rare, devastating tornadoes that can level well-built homes entirely.",
        "The rating estimates wind speed from the severity of the destruction along the tornado's path."
      ]
    },
    {
      "type": "heading",
      "text": "Why Tornado Alley Exists"
    },
    {
      "type": "paragraph",
      "text": "The central United States experiences more tornadoes than anywhere else on Earth, averaging on the order of a thousand a year across the country. The loose region from Texas up through Oklahoma, Kansas and Nebraska is nicknamed Tornado Alley, and its geography is almost purpose-built for severe storms. Warm, humid air streams north from the Gulf of Mexico. High above it, hot dry air rolls off the Rocky Mountains and the deserts of the Southwest. Where these air masses collide, often with the jet stream adding strong winds aloft, the atmosphere becomes intensely unstable and sheared — ideal conditions for supercells."
    },
    {
      "type": "paragraph",
      "text": "Tornadoes are far from a North American monopoly, though. They occur on every continent except Antarctica, with notable activity in parts of South America, southern Africa, Bangladesh and Europe. They are simply most frequent and most violent where the geography concentrates the ingredients as efficiently as the American plains do."
    },
    {
      "type": "heading",
      "text": "Reading the Sky and the Land"
    },
    {
      "type": "paragraph",
      "text": "You will not catch a tornado in a still satellite image, but the landscapes that breed them are recognisable: wide, flat agricultural plains under big skies, dotted with storm shelters and resilient single-storey buildings. Understanding why those plains are so storm-prone adds depth to the way you read a place — the same instinct for connecting landscape and climate that quietly sharpens your guesses in EarthGuessr."
    }
  ]
};
